import React, { useState, useEffect } from 'react';
import { HeaderProps, HeaderState } from './models';
import DesktopNavigation from './components/DesktopNavigation';
import MobileNavigation from './components/MobileNavigation';
import './styles.scss';

const Header: React.FC<HeaderProps> = ({ 
  primaryNavigation, 
  searchConfig, 
  logoConfig, 
  component,  
  clearIcon,
  countrySelectorUrl,
  svgCountryIcon,
}) => {
  const [state, setState] = useState<HeaderState>({
    isMobileMenuOpen: false,
    isSearchOpen: false
  });

  // Close mobile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (state.isMobileMenuOpen) {
        const target = event.target as Element;
        const menuContentWrapper = document.querySelector('.menu-content-wrapper');
        const hamburgerButton = target.closest('.ge_icon-menu-item__hamburger-button');
        
        // Close menu if clicking outside menu-content-wrapper and not on hamburger button
        if (
          menuContentWrapper &&
          !menuContentWrapper.contains(target) &&
          !hamburgerButton
        ) {
          setState(prev => ({
            ...prev,
            isMobileMenuOpen: false
          }));
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [state.isMobileMenuOpen]);

  // Toggle mobile menu
  const toggleMobileMenu = () => {
    setState(prev => ({
      ...prev,
      isMobileMenuOpen: !prev.isMobileMenuOpen
    }));
  };

  const handleSearchClick = () => {
    if (searchConfig?.searchUrl) {
      globalThis.location.href = searchConfig.searchUrl;
    } else {
      setState(prev => ({
        ...prev,
        isSearchOpen: !prev.isSearchOpen
      }));
    }
  };



  // Handle logo click
  const handleLogoClick = () => {
    globalThis.location.href = logoConfig?.logoLink || '/';
  };

  // Resolve logo URL using Dynamic Media (serverPath + assetPath)
  const getLogoUrl = () => {
    const ref = logoConfig?.logoReference;
    if (!ref) return '';
    if (typeof ref === 'string' && ref.startsWith('/images/')) {
      return ref;
    }
    if (ref && typeof ref === 'object' && ref !== null) {
      const refObj = ref as any;
      if (refObj.serverPath && refObj.assetPath) {
        return refObj.serverPath + refObj.assetPath;
      }
      const assetName = refObj.assetName;
      return assetName?.startsWith('/images/') ? assetName : '/images/gehealthcarestage/' + assetName;
    }
    return '';
  };

  return (
    <header className="ge-header-component" id="ge-header">
      <div className="ge-header-wrap">
        <div className="ge_header-wrapper">
          {/* Desktop Logo */}
          <div className="ge_desktop-logo">
            <div className="ge_logo-container">
              <a
                href={logoConfig?.logoLink || '/'}
                aria-label={logoConfig?.logoAlt || 'GE Logo'}
                onClick={handleLogoClick}
              >
                <img 
                  src={getLogoUrl()} 
                  alt={logoConfig?.logoAlt || 'GE Logo'} 
                />
              </a>
            </div>
          </div>

          {/* Desktop Primary Navigation */}
          <DesktopNavigation
            navigationItems={primaryNavigation}
            component={component}
          />

          {/* Icons Container */}
          <div className="ge_icons-container">
            <ul className="ge_icons-container__icons-list">
              {/* Search Icon */}
              {searchConfig?.isEnabled && (
                <li className="ge_icon-menu-item">
                  {searchConfig?.searchUrl && searchConfig?.searchIcon && (
                    <button
                      className="ge_icon-menu-item__icon-button search-icon-link"
                      aria-label="Search Icon"
                      id="searchIcon"
                      onClick={handleSearchClick}
                      dangerouslySetInnerHTML={{ __html: searchConfig.searchIcon }}
                    >
                    </button>
                  )}
                </li>
              )}

              {/* China country selector: globe + CN/CN, opens the sitemap in a new tab */}
              {countrySelectorUrl && (
                <li className="ge_icon-menu-item china-country-selector-item">
                  <a
                    className="ge_icon-menu-item__icon-button china-country-selector-link"
                    href={countrySelectorUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="China Country Selector"
                  >
                    {svgCountryIcon && (
                      <span
                        className="china-country-selector-icon"
                        dangerouslySetInnerHTML={{ __html: svgCountryIcon }}
                      />
                    )}
                    <span className="ge_china-cs-label">CN / CN</span>
                  </a>
                </li>
              )}

              <li className="ge_icon-menu-item ge-hamburger-icon">
                <button 
                  className="ge_icon-menu-item__hamburger-button"
                  onClick={toggleMobileMenu}
                  aria-label={state.isMobileMenuOpen ? "Close Menu" : "Open Menu"}
                >
                  {!state.isMobileMenuOpen && (
                    <svg width="16" height="16" viewBox="0 0 16 16">
                      <g>
                        <path d="M16,4H0V2H16Zm0,3H0V9H16Zm0,5H0v2H16Z"></path>
                      </g>
                    </svg>
                  )}
                  {state.isMobileMenuOpen && (
                    <span className="ge_icon-menu-item__close-icon" dangerouslySetInnerHTML={{ __html: clearIcon }} />
                  )}
                </button>
              </li>
            </ul>
          </div>
        </div>

        <MobileNavigation
          navigationItems={primaryNavigation}
          isVisible={state.isMobileMenuOpen}
        />
      </div>
    </header>
  );
};

export default Header;